// src/operator/console/tenant-object-rbac.tsx
//
// Sous-onglet « Droits & Stratégies » du workspace de paramétrage
// client/site. Trois blocs :
//   1. Grants RBAC : qui peut faire quoi sur ce client/site
//      (viewer / operator / manager), CRUD sur operator_grants.
//   2. Stratégie (policy) : JSON fusionné par host-policy-resolver le long
//      de la hiérarchie tenant > site > client > host. Édition du policy du
//      scope courant.
//   3. Partages inter-tenants (clients uniquement) : client_tenant_shares.

import { createSignal, createResource, Show, For, type Component } from "solid-js";
import { useNotify } from "../shared/notify";
import { useConfirm } from "../shared/confirm-dialog";
import {
  fetchGrants,
  fetchOperators,
  fetchTenants,
  fetchShares,
  createGrant,
  deleteGrant,
  createShare,
  deleteShare,
  patchClient,
  patchSite,
  RBAC_ROLES,
  ACCESS_LEVELS,
  type Grant,
  type Operator,
  type Tenant,
  type Share,
} from "./tenant-api";

interface Props {
  objectType: "client" | "site";
  objectId: string;
  objectLabel: string;
  policy: Record<string, unknown> | null;
  onPolicySaved: () => void;
}

export const TenantObjectRbac: Component<Props> = (props) => {
  const notify = useNotify();
  const confirmDialog = useConfirm();

  // ── Grants RBAC ──
  const [grants, { refetch: refetchGrants }] = createResource<Grant[]>(() => fetchGrants());
  const [operators] = createResource<Operator[]>(() => fetchOperators());

  const scopedGrants = () => {
    const all = grants() ?? [];
    return all.filter((g) => g.scopeType === props.objectType && g.scopeId === props.objectId);
  };

  const operatorLabel = (userId: string) => {
    const op = (operators() ?? []).find((o) => o.id === userId);
    return op ? `${op.name || op.email}` : userId.slice(0, 8);
  };

  // Formulaire nouveau grant
  const [newGrantUserId, setNewGrantUserId] = createSignal("");
  const [newGrantRole, setNewGrantRole] = createSignal<string>("viewer");
  const [addingGrant, setAddingGrant] = createSignal(false);

  async function addGrant() {
    if (!newGrantUserId() || addingGrant()) return;
    setAddingGrant(true);
    try {
      await createGrant({
        userId: newGrantUserId(),
        scopeType: props.objectType,
        scopeId: props.objectId,
        role: newGrantRole(),
      });
      setNewGrantUserId("");
      setNewGrantRole("viewer");
      await refetchGrants();
      notify.ok("Grant ajouté", { durationMs: 2500 });
    } catch (err) {
      notify.danger("Échec de l'ajout du grant", {
        hint: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setAddingGrant(false);
    }
  }

  async function removeGrant(g: Grant) {
    const ok = await confirmDialog.confirm({
      title: "Retirer ce grant ?",
      body: `${operatorLabel(g.userId)} ne sera plus ${g.role} sur ${props.objectLabel}.`,
      confirmLabel: "Retirer",
      level: "warn",
    });
    if (!ok) return;
    try {
      await deleteGrant(g.id);
      await refetchGrants();
      notify.ok("Grant retiré", { durationMs: 2500 });
    } catch (err) {
      notify.danger("Échec du retrait", { hint: err instanceof Error ? err.message : String(err) });
    }
  }

  // ── Stratégie (policy) ──
  const [policyText, setPolicyText] = createSignal("");
  const [policyDirty, setPolicyDirty] = createSignal(false);
  const [policySaving, setPolicySaving] = createSignal(false);

  // Initialise l'éditeur quand la policy arrive / change.
  createResource(() => {
    const p = props.policy;
    const text = p && Object.keys(p).length > 0 ? JSON.stringify(p, null, 2) : "{\n  \n}";
    setPolicyText(text);
    setPolicyDirty(false);
    return null;
  });

  function onPolicyInput(v: string) {
    setPolicyText(v);
    setPolicyDirty(true);
  }

  async function savePolicy() {
    if (policySaving()) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(policyText());
    } catch (err) {
      notify.danger("JSON invalide", { hint: err instanceof Error ? err.message : String(err) });
      return;
    }
    setPolicySaving(true);
    try {
      if (props.objectType === "client") {
        await patchClient(props.objectId, { policy: parsed as Record<string, unknown> });
      } else {
        await patchSite(props.objectId, { policy: parsed as Record<string, unknown> });
      }
      setPolicyDirty(false);
      props.onPolicySaved();
      notify.ok("Stratégie enregistrée", { durationMs: 2500 });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      notify.danger("Échec de l'enregistrement", { hint: msg });
    } finally {
      setPolicySaving(false);
    }
  }

  // ── Partages inter-tenants (clients uniquement) ──
  const [shares, { refetch: refetchShares }] = createResource<Share[]>(async () => {
    if (props.objectType !== "client") return [];
    return fetchShares(props.objectId);
  });
  const [tenants] = createResource<Tenant[]>(() => fetchTenants());

  const tenantLabel = (tenantId: string) => {
    const t = (tenants() ?? []).find((x) => x.id === tenantId);
    return t ? t.name : tenantId.slice(0, 8);
  };

  const [newShareTenant, setNewShareTenant] = createSignal("");
  const [newShareLevel, setNewShareLevel] = createSignal<string>("read_only");
  const [addingShare, setAddingShare] = createSignal(false);

  async function addShare() {
    if (props.objectType !== "client" || !newShareTenant() || addingShare()) return;
    setAddingShare(true);
    try {
      await createShare({
        clientId: props.objectId,
        tenantId: newShareTenant(),
        accessLevel: newShareLevel(),
      });
      setNewShareTenant("");
      setNewShareLevel("read_only");
      await refetchShares();
      notify.ok("Partage ajouté", { durationMs: 2500 });
    } catch (err) {
      notify.danger("Échec du partage", { hint: err instanceof Error ? err.message : String(err) });
    } finally {
      setAddingShare(false);
    }
  }

  async function removeShare(s: Share) {
    if (props.objectType !== "client") return;
    const ok = await confirmDialog.confirm({
      title: "Retirer ce partage ?",
      body: `Le tenant ${tenantLabel(s.tenantId)} ne pourra plus accéder à ${props.objectLabel}.`,
      confirmLabel: "Retirer",
      level: "warn",
    });
    if (!ok) return;
    try {
      await deleteShare(props.objectId, s.tenantId);
      await refetchShares();
      notify.ok("Partage retiré", { durationMs: 2500 });
    } catch (err) {
      notify.danger("Échec du retrait", { hint: err instanceof Error ? err.message : String(err) });
    }
  }

  return (
    <section class="tenant-section">
      {/* ── Grants RBAC ── */}
      <div class="tenant-block">
        <h3>🔐 Droits d'accès (RBAC)</h3>
        <p class="tenant-block__hint">
          Les opérateurs listés ici ont un accès délégué sur ce {props.objectType === "client" ? "client" : "site"}.
          Rôles : <code>viewer</code> (lecture), <code>operator</code> (prise en main), <code>manager</code> (gestion).
        </p>
        <table class="rmm-table tenant-rbac-table">
          <thead>
            <tr>
              <th>Opérateur</th>
              <th>Rôle</th>
              <th>Depuis</th>
              <th>Expire</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <For
              each={scopedGrants()}
              fallback={
                <tr>
                  <td colspan={5} class="tenant-empty-cell">
                    Aucun grant explicite. L'accès peut venir du tenant propriétaire ou d'un partage.
                  </td>
                </tr>
              }
            >
              {(g) => (
                <tr>
                  <td>{operatorLabel(g.userId)}</td>
                  <td>
                    <span class={`tag tag--role-${g.role}`}>{g.role}</span>
                  </td>
                  <td>{g.grantedAt ? new Date(g.grantedAt).toLocaleDateString() : "—"}</td>
                  <td>{g.expiresAt ? new Date(g.expiresAt).toLocaleDateString() : "—"}</td>
                  <td>
                    <button class="btn btn--xs" onClick={() => removeGrant(g)}>
                      Retirer
                    </button>
                  </td>
                </tr>
              )}
            </For>
          </tbody>
        </table>

        <div class="tenant-add-row">
          <select value={newGrantUserId()} onChange={(e) => setNewGrantUserId(e.currentTarget.value)}>
            <option value="">— Sélectionner un opérateur —</option>
            <For each={operators() ?? []}>{(op) => <option value={op.id}>{op.name || op.email}</option>}</For>
          </select>
          <select value={newGrantRole()} onChange={(e) => setNewGrantRole(e.currentTarget.value)}>
            <For each={[...RBAC_ROLES]}>{(r) => <option value={r}>{r}</option>}</For>
          </select>
          <button class="btn btn--primary btn--sm" onClick={addGrant} disabled={!newGrantUserId() || addingGrant()}>
            {addingGrant() ? "…" : "Ajouter"}
          </button>
        </div>
      </div>

      {/* ── Stratégie (policy JSON) ── */}
      <div class="tenant-block">
        <h3>📋 Stratégie d'accès</h3>
        <p class="tenant-block__hint">
          Politique JSON appliquée à ce {props.objectType === "client" ? "client" : "site"} et fusionnée le long de la
          hiérarchie (tenant › client › site › hôte) par le résolveur de politiques. Permet de restreindre les outils,
          horaires, etc.
        </p>
        <textarea
          class="tenant-policy-editor"
          value={policyText()}
          onInput={(e) => onPolicyInput(e.currentTarget.value)}
          spellcheck={false}
          rows={10}
        />
        <div class="tenant-policy-actions">
          <button class="btn btn--primary btn--sm" onClick={savePolicy} disabled={!policyDirty() || policySaving()}>
            {policySaving() ? "Enregistrement…" : "Enregistrer la stratégie"}
          </button>
          <Show when={policyDirty()}>
            <span class="tenant-unsaved">modifications non enregistrées</span>
          </Show>
        </div>
      </div>

      {/* ── Partages inter-tenants (clients uniquement) ── */}
      <Show when={props.objectType === "client"}>
        <div class="tenant-block">
          <h3>🔗 Partages inter-tenants</h3>
          <p class="tenant-block__hint">
            Partage ce client avec d'autres tenants (organisations). <code>read_only</code> = lecture seule,{" "}
            <code>operate</code> = prise en main autorisée.
          </p>
          <table class="rmm-table tenant-rbac-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Niveau</th>
                <th>Depuis</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <For
                each={shares() ?? []}
                fallback={
                  <tr>
                    <td colspan={4} class="tenant-empty-cell">
                      Aucun partage.
                    </td>
                  </tr>
                }
              >
                {(s) => (
                  <tr>
                    <td>{tenantLabel(s.tenantId)}</td>
                    <td>
                      <span class={`tag tag--share-${s.accessLevel}`}>{s.accessLevel}</span>
                    </td>
                    <td>{s.grantedAt ? new Date(s.grantedAt).toLocaleDateString() : "—"}</td>
                    <td>
                      <button class="btn btn--xs" onClick={() => removeShare(s)}>
                        Retirer
                      </button>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
          <div class="tenant-add-row">
            <select value={newShareTenant()} onChange={(e) => setNewShareTenant(e.currentTarget.value)}>
              <option value="">— Sélectionner un tenant —</option>
              <For each={tenants() ?? []}>{(t) => <option value={t.id}>{t.name}</option>}</For>
            </select>
            <select value={newShareLevel()} onChange={(e) => setNewShareLevel(e.currentTarget.value)}>
              <For each={[...ACCESS_LEVELS]}>{(l) => <option value={l}>{l}</option>}</For>
            </select>
            <button class="btn btn--primary btn--sm" onClick={addShare} disabled={!newShareTenant() || addingShare()}>
              {addingShare() ? "…" : "Partager"}
            </button>
          </div>
        </div>
      </Show>
    </section>
  );
};
